import { Card, Fade, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { Emoji } from "emoji-mart";
import React, { useState } from "react";
import FadeIn from "react-fade-in";
import { format } from "../../../../utils/Date";
import { FeaturedBadge } from "../../../Stickers/MarketplaceCard";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

const MainContainer = styled(Card)(({ featured }) => ({
  flexDirection: "column",
  display: "flex",
  height: "380px",
  boxSizing: "border-box",
  padding: "24px",
  textAlign: "left",
  borderRadius: "16px",
  cursor: "pointer",
  backgroundImage: featured ? 'url("images/featured.jpg")' : 'none',
  objectFit: "cover",
  color: featured ? "white" : "#ffffff",
  border: "1px solid #333333",
  boxShadow: "none",
  transition: "all 0.3s ease",
  backgroundColor: "#1e1e1e",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "6px 6px 0px 0px rgba(255, 255, 255, 0.1)",
    border: "1px solid #fff",
  },
  "@media (max-width: 650px)": {
    maxWidth: "calc(100vw - 32px)",
  },
}));

const HideTextOnOverflow = styled(Typography)({
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  width: "100%",
  overflow: "hidden",
});

const Name = styled(HideTextOnOverflow)(({ featured }) => ({
  fontSize: "18px !important",
  fontWeight: featured ? "400" : "400",
  marginTop: "1rem",
  marginBottom: "0.25rem",
}));

const Author = styled(HideTextOnOverflow)({
  fontSize: "13px",
  marginBottom: "0.75rem",
  color: "#666",
});

const AuthorName = styled("span")({
  color: "#2196F3",
  textDecoration: "underline",
});

const Description = styled(HideTextOnOverflow)(({ featured }) => ({
  color: featured ? "white" : "#a0a0a0",
  fontSize: "12px",
  fontWeight: 500,
  display: "-webkit-box",
  whiteSpace: "normal !important",
  marginBottom: "0.5rem",
  wordWrap: "break-word",
  "-webkit-line-clamp": featured ? "12" : "2",
  "-webkit-box-orient": "vertical",
  "@media (max-width: 650px)": {
    marginBottom: featured && "0",
  },
}));

const Categories = styled(HideTextOnOverflow)({
  fontSize: "12px",
  marginBottom: "0.5rem",
  color: "#2196F3",
  fontWeight: "500",
});

const Downloads = styled(Typography)({
  fontSize: "12px",
  paddingBottom: "6px",
});

const MetaContainer = styled("div")({
  marginTop: "auto",
  borderTop: "1px solid #333333",
  paddingTop: "8px",
});

/**
 *
 * @param {String} name Name of template
 * @param {String} description Description of template
 * @param {String} lastUpdated Time that the template was last updated
 * @param {Array} categories Categories of the template
 * @param {Number} downloads Number of downloads
 * @param {Boolean} featured Checks if card is featured
 * @param {String} image Emoji icon
 * @param {String} href Link to the template
 * @param {Object} categoryMapping Contains mapping of categoryID -> Name
 * @returns Card for marketplace template
 */
const MarketplaceCard = ({
  name,
  author,
  description,
  lastModified,
  categories,
  downloads,
  featured = false,
  image,
  href,
  categoryMapping,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCardClick = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const renderEmoji = (imagePath) => {
    // Checks if image path is not an emoji
    if (!imagePath || imagePath.charAt(0) !== ":") {
      return ":twisted_rightwards_arrows:";
    } else {
      return imagePath;
    }
  };

  const renderCategories = (categories) => {
    let categoriesString = "";

    if (!categories) {
      return "";
    }

    categories.forEach((category, idx) => {
      if (idx === categories.length - 1) {
        categoriesString += " " + categoryMapping[category];
        return;
      }
      categoriesString += categoryMapping[category] + " | ";
    });

    return categoriesString;
  };

  return (
    <FadeIn>
      <a 
        onClick={handleCardClick} 
        style={{ textDecoration: "none" }} 
        href={href}
      >
        <MainContainer
          button="true"
          featured={featured}
          backgroundimage={image}
        >
          {featured && <FeaturedBadge />}
          {!featured && (
            <Emoji set="twitter" size={36} emoji={renderEmoji(image)} />
          )}
          <Tooltip title={name} placement="bottom-start">
            <Name featured={featured}>{name}</Name>
          </Tooltip>
          {!featured && (
            <Tooltip title={author} placement="bottom-start">
              <Author>
                By <AuthorName>{author}</AuthorName>
              </Author>
            </Tooltip>
          )}
          <Tooltip title={description} placement="bottom-start">
            <Description featured={featured}>{description}</Description>
          </Tooltip>
          <Tooltip
            title={renderCategories(categories)}
            placement="bottom-start"
          >
            <Categories>{renderCategories(categories)}</Categories>
          </Tooltip>
          {!featured && (
            <MetaContainer>
              <Downloads>{downloads} downloads</Downloads>
            </MetaContainer>
          )}
        </MainContainer>
      </a>

      <Dialog
        open={isModalOpen}
        fullWidth
        maxWidth="xl"
        onClose={() => setIsModalOpen(false)}
      >
        <DialogContent sx={{ p: 0 }}>
          <iframe 
            src={href}
            style={{
              width: "100%",
              height: "80vh",
            }}
          />
        </DialogContent>
      </Dialog>
    </FadeIn>
  );
};

export default MarketplaceCard;
